/**
 * AI recommendation service that calls the real backend API
 * Returns AIRecommendationResponseDto as provided by the backend.
 */
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';

/**
 * Fetch AI recommendation for a problem from the backend API
 * @param {number} problemId - Problem ID
 * @param {string} clientId - Client ID
 * @param {number} storeId - Store ID (kept for signature compatibility)
 * @param {number} productId - Product ID (kept for signature compatibility)
 * @returns {Promise<Object>} AI recommendation data
 */
export const fetchAIRecommendation = async (problemId, clientId, storeId, productId) => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
  if (!problemId) throw new Error('problemId is required');
  if (!clientId) throw new Error('clientId is required');
  if (!token) throw new Error('No access token available');

  const response = await axios.post(
    `${API_BASE_URL}/problems/${problemId}/ai-recommendation?clientId=${clientId}`,
    { includeReasoning: true },
    { headers: createAuthHeader(token) }
  );

  return response.data;
};

/**
 * Calculate discounted price
 * @param {number} originalPrice - Original price
 * @param {number} discount - Discount percentage (0-100)
 * @returns {number} Discounted price
 */
export const calculateDiscountedPrice = (originalPrice, discount) => {
  if (!originalPrice || !discount || discount < 0) return originalPrice;
  return originalPrice * (1 - discount / 100);
};

/**
 * Validate discount input
 * @param {string|number} discount - Discount value to validate
 * @returns {Object} Validation result with isValid and error message
 */
export const validateDiscount = (discount) => {
  const numericDiscount = parseFloat(discount);
  
  if (isNaN(numericDiscount)) {
    return { isValid: false, error: 'El descuento debe ser un número' };
  }
  
  if (numericDiscount < 5) {
    return { isValid: false, error: 'El descuento mínimo es 5%' };
  }
  
  if (numericDiscount > 30) {
    return { isValid: false, error: 'El descuento máximo es 30%' };
  }
  
  return { isValid: true, error: null };
};
